import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Tag,
  Space,
  Descriptions,
  Typography,
  Divider,
  Badge,
  Empty,
  Button,
  Spin,
  Timeline,
  Drawer,
  Alert,
  DatePicker,
} from 'antd';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {
  Package,
  Activity,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader,
  Calendar,
  User,
  MapPin,
  Hash,
  RefreshCw,
  ChevronRight,
  Droplet,
  PlayCircle,
  StopCircle,
  Info,
} from 'lucide-react';
import { get_equipment_logs, get_equipment_list_for_cleaning } from '@/utils/api';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import StatCard from '@/components/StatCard';
import TableContainer from '@/components/TableContainer';
import { formatDateTime } from '@/utils/datetime';

dayjs?.extend(duration);

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Same semantic KPI palette as the PR List (TicketList.js) — feeds the shared StatCard
// component so every KPI of the same "meaning" looks consistent. Extended with two more
// entries (accent/accent2) since this page has 6 metrics vs. PR List's 5.
const EQUIPMENT_KPI_COLORS = {
  primary: { icon: '#64748b', bgLight: '#f1f5f9', bgDark: '#1e293b' }, // slate
  info: { icon: '#6366f1', bgLight: '#e0e7ff', bgDark: '#312e81' }, // indigo
  success: { icon: '#10b981', bgLight: '#d1fae5', bgDark: '#064e3b' }, // emerald
  warning: { icon: '#f59e0b', bgLight: '#fef3c7', bgDark: '#78350f' }, // amber
  accent: { icon: '#06b6d4', bgLight: '#cffafe', bgDark: '#164e63' }, // cyan
  accent2: { icon: '#0ea5e9', bgLight: '#e0f2fe', bgDark: '#0c4a6e' }, // sky
};

const getEquipmentKpiColors = (type, isDark) => {
  const c = EQUIPMENT_KPI_COLORS[type] || EQUIPMENT_KPI_COLORS.primary;
  return { iconColor: c.icon, bgColor: isDark ? c.bgDark : c.bgLight };
};

const EquipmentLogs = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);

  // Dark mode colors
  const drawerBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const cardBgColor = isDark ? defaultColors.palette.gray[700] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const descriptionsBgColor = isDark ? 'transparent' : '#fafafa';
  const descriptionsLabelBg = isDark ? 'rgba(255,255,255,0.1)' : '#fafafa';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const timelineDescBgColor = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const timelineDescLabelBg = isDark ? 'rgba(255,255,255,0.1)' : '#f5f5f5';
  const [equipmentList, setEquipmentList] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [logData, setLogData] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Fetch equipment list on mount
  useEffect(() => {
    fetchEquipmentList();
  }, []);

  const fetchEquipmentList = async () => {
    try {
      await get_equipment_list_for_cleaning((res) => {
        if (res.data?.status === 'success' && res.data?.data) {
          setEquipmentList(res.data.data);
        } else {
          toast.warning('No equipment found');
          setEquipmentList([]);
        }
      });
    } catch (error) {
      console.error('Error fetching equipment list:', error);
      toast.error('Failed to load equipment list');
      setEquipmentList([]);
    }
  };

  const fetchEquipmentLogs = async (equipmentId, startDate, endDate) => {
    setLoading(true);
    try {
      const params = {
        equipment_id: equipmentId,
        start_date: startDate,
        end_date: endDate,
      };

      await get_equipment_logs(params, (res) => {
        if (res.status === 'success') {
          setLogData(res.data);
          toast.success('Equipment logs loaded successfully');
        } else {
          toast.error(res.message || 'Failed to fetch equipment logs');
          setLogData(null);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error fetching equipment logs:', error);
      toast.error('Failed to load equipment logs');
      setLoading(false);
    }
  };

  const handleEquipmentChange = (equipmentId) => {
    setSelectedEquipment(equipmentId);
    // if (equipmentId && dateRange && dateRange[0] && dateRange[1]) {
    //   const startDate = dateRange[0].format('YYYY-MM-DD');
    //   const endDate = dateRange[1].format('YYYY-MM-DD');
    //   // fetchEquipmentLogs(equipmentId, startDate, endDate);
    // } else {
    //   setLogData(null);
    // }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (selectedEquipment && dates && dates[0] && dates[1]) {
      const startDate = dates[0].format('YYYY-MM-DD');
      const endDate = dates[1].format('YYYY-MM-DD');
      fetchEquipmentLogs(selectedEquipment, startDate, endDate);
    }
  };

  const handleRefresh = () => {
    if (selectedEquipment && dateRange && dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      fetchEquipmentLogs(selectedEquipment, startDate, endDate);
      toast.info('Refreshing equipment logs...');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      Completed: { color: 'success', icon: <CheckCircle2 size={16} />, text: 'Completed' },
      'In Progress': { color: 'processing', icon: <Loader size={16} />, text: 'In Progress' },
      Pending: { color: 'warning', icon: <Clock size={16} />, text: 'Pending' },
      Failed: { color: 'error', icon: <AlertCircle size={16} />, text: 'Failed' },
    };
    return configs[status] || configs['Pending'];
  };

  const getMachineStatusConfig = (status) => {
    const configs = {
      Available: {
        color: 'success',
        bgColor: isDark ? '#14532d' : '#f0fdf4',
        borderColorVal: isDark ? '#166534' : '#bbf7d0',
      },
      Occupied: {
        color: 'processing',
        bgColor: isDark ? '#1e3a5f' : '#eff6ff',
        borderColorVal: isDark ? '#1e40af' : '#bfdbfe',
      },
      Cleaning: {
        color: 'warning',
        bgColor: isDark ? '#713f12' : '#fefce8',
        borderColorVal: isDark ? '#a16207' : '#fef08a',
      },
      Maintenance: {
        color: 'default',
        bgColor: isDark ? defaultColors.palette.gray[700] : '#f9fafb',
        borderColorVal: isDark ? defaultColors.palette.gray[600] : '#e5e7eb',
      },
      'Under Repair': {
        color: 'error',
        bgColor: isDark ? '#7f1d1d' : '#fef2f2',
        borderColorVal: isDark ? '#991b1b' : '#fecaca',
      },
    };
    return configs[status] || configs['Available'];
  };

  const getLogTypeIcon = (logType) => {
    const icons = {
      MACHINE_STATUS_CHANGE: <Activity size={16} className="text-blue-600" />,
      CLEANING_ACTIVITY: <Droplet size={16} className="text-cyan-600" />,
      CLEANING_APPROVAL: <CheckCircle2 size={16} className="text-green-600" />,
      BATCH_START: <PlayCircle size={16} className="text-green-600" />,
      BATCH_END: <StopCircle size={16} className="text-red-600" />,
    };
    return icons[logType] || <Info size={16} className="text-gray-600" />;
  };

  const hasValue = (v) => v !== null && v !== undefined && v !== '' && v !== 'N/A';

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Ongoing';
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    const diff = end.diff(start);
    const duration = dayjs.duration(diff);

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const batchColumns = [
    {
      title: 'Batch Number',
      dataIndex: 'batch_number',
      key: 'batch_number',
      width: 180,
      render: (text) => (
        <Text strong className="text-blue-600">
          {text}
        </Text>
      ),
    },
    {
      title: 'MBMR Name',
      dataIndex: 'recipe_name',
      key: 'recipe_name',
      width: 150,
    },
    {
      title: 'Product',
      key: 'product',
      width: 150,
      render: (_, record) => (
        <div>
          <Text className="block text-sm">{record.product_name}</Text>
          <Text type="secondary" className="text-xs">
            {record.product_code}
          </Text>
        </div>
      ),
    },
    {
      title: 'Start Time',
      dataIndex: 'batch_start_time',
      key: 'batch_start_time',
      width: 180,
      render: (time) =>
        time ? (
          <Space size={4}>
            <Calendar size={14} className="text-gray-400" />
            <Text className="text-xs">{formatDateTime(time, 'DD-MMM-YYYY HH:mm')}</Text>
          </Space>
        ) : null,
    },
    {
      title: 'End Time',
      dataIndex: 'batch_end_time',
      key: 'batch_end_time',
      width: 180,
      render: (time) =>
        time ? (
          <Space size={4}>
            <Calendar size={14} className="text-gray-400" />
            <Text className="text-xs">{formatDateTime(time, 'DD-MMM-YYYY HH:mm')}</Text>
          </Space>
        ) : (
          <Tag
            color="orange"
            className="inline-flex items-center gap-1 text-xs px-4 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-300"
          >
            <Clock size={12} />
            Ongoing
          </Tag>
        ),
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 100,
      render: (_, record) => (
        <Tag color="blue" className="text-xs rounded-full">
          {formatDuration(record.batch_start_time, record.batch_end_time)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'batch_status',
      key: 'batch_status',
      width: 130,
      render: (status) => {
        const config = getStatusConfig(status);
        return (
          <Tag
            color={config.color}
            icon={config.icon}
            className="inline-flex items-center gap-1 text-xs px-4 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-300"
          >
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Logs',
      dataIndex: 'logs',
      key: 'logs',
      width: 100,
      align: 'center',
      render: (logs) => (
        <Badge count={logs?.length || 0} showZero color="blue" className="cursor-pointer" />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => {
            setSelectedBatch(record);
            setDrawerVisible(true);
          }}
          icon={<ChevronRight size={24} />}
          className="flex items-center"
        >
          View Details
        </Button>
      ),
    },
  ];

  const renderEquipmentDetails = () => {
    if (!logData?.equipment_details) return null;

    const { equipment_details } = logData;
    const statusConfig = getMachineStatusConfig(equipment_details.current_status);

    // Same fields as before, but rendered as one single-line strip (name/type + every
    // populated detail field + the status tag together) instead of a title row followed
    // by a separate grid row underneath.
    const detailFields = [
      hasValue(equipment_details.equipment_code) && {
        key: 'code',
        icon: <Hash size={14} />,
        label: 'Equipment ID',
        value: <Text strong>{equipment_details.equipment_code}</Text>,
      },
      hasValue(equipment_details.area) && {
        key: 'area',
        icon: <MapPin size={14} />,
        label: 'Area',
        value: equipment_details.area,
      },
      hasValue(equipment_details.location) && {
        key: 'location',
        icon: <MapPin size={14} />,
        label: 'Location',
        value: equipment_details.location,
      },
      hasValue(equipment_details.created_by?.employee_name) && {
        key: 'created_by',
        icon: <User size={14} />,
        label: 'Created By',
        value: equipment_details.created_by.employee_name,
      },
      hasValue(equipment_details.created_at) && {
        key: 'created_at',
        icon: <Calendar size={14} />,
        label: 'Created At',
        value: formatDateTime(equipment_details.created_at, 'DD-MMM-YYYY HH:mm:ss'),
      },
    ].filter(Boolean);

    return (
      <Card className="mb-6 shadow-sm border-l-4 border-blue-500">
        {/* One flat flex row, justify-between across EVERY item (not just left-group vs.
            tag), so the gap between each field — and between the last field and the
            status tag — is the same equal distance. */}
        <div className="flex items-center flex-nowrap justify-between overflow-x-auto">
          <div style={{ whiteSpace: 'nowrap' }}>
            <Title level={5} className="!mb-0 text-blue-700" style={{ lineHeight: 1.2 }}>
              {equipment_details.equipment_name}
            </Title>
            <Text type="secondary" className="text-xs">
              {equipment_details.equipment_type} - {equipment_details.machine_category}
            </Text>
          </div>
          {detailFields.map((f) => (
            <div key={f.key} style={{ whiteSpace: 'nowrap' }}>
              <div
                className="flex items-center gap-1.5"
                style={{ color: secondaryTextColor, fontSize: 12 }}
              >
                {f.icon}
                {f.label}
              </div>
              <div style={{ color: textColor, fontWeight: 500 }}>{f.value}</div>
            </div>
          ))}
          <Tag
            color={statusConfig.color}
            className="px-4 py-1 text-sm font-semibold border"
            style={{
              backgroundColor: statusConfig.bgColor,
              borderColor: statusConfig.borderColorVal,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {equipment_details.current_status}
          </Tag>
        </div>
      </Card>
    );
  };

  const renderSummary = () => {
    if (!logData?.summary) return null;

    const { summary } = logData;

    return (
      <div className="mb-6">
        <Title level={5} className="mb-4">
          <Package size={20} className="inline mr-2 text-blue-600" />
          Summary Statistics
        </Title>
        {/* Same StatCard + semantic-color pattern as the PR List (TicketList.js) — no
            wrapping Card/border here either, matching how PR List places its StatCard
            row directly, unwrapped. */}
        <div className="flex flex-nowrap gap-3">
          <StatCard
            label="Total Batches"
            value={summary.total_batches}
            icon={<Package />}
            {...getEquipmentKpiColors('primary', isDark)}
          />
          <StatCard
            label="Total Logs"
            value={summary.total_logs}
            icon={<Activity />}
            {...getEquipmentKpiColors('info', isDark)}
          />
          <StatCard
            label="Completed"
            value={summary.batches_completed}
            icon={<CheckCircle2 />}
            {...getEquipmentKpiColors('success', isDark)}
          />
          <StatCard
            label="In Progress"
            value={summary.batches_in_progress}
            icon={<Loader />}
            {...getEquipmentKpiColors('warning', isDark)}
          />
          <StatCard
            label="Status Changes"
            value={summary.total_status_changes}
            icon={<Activity />}
            {...getEquipmentKpiColors('accent', isDark)}
          />
          <StatCard
            label="Cleaning Activities"
            value={summary.total_cleaning_activities}
            icon={<Droplet />}
            {...getEquipmentKpiColors('accent2', isDark)}
          />
        </div>
      </div>
    );
  };

  const renderBatchLogs = () => {
    if (!logData?.batches || logData.batches.length === 0) {
      return (
        <Card className="mt-6">
          <Empty description="No batch logs found for this equipment" />
        </Card>
      );
    }

    return (
      <Card className="mt-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <Title level={5} className="!mb-0">
            <Activity size={20} className="inline mr-2 text-blue-600" />
            Batch Processing History
          </Title>
          <Tag color="blue" className="text-sm">
            {logData.batches.length} Batches
          </Tag>
        </div>

        <TableContainer
          columns={batchColumns}
          data={logData.batches}
          rowKey="batch_number"
          pagination={{
            pageSize: 10,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} batches`,
          }}
          // No max-height cap — original had no scroll.y either; pagination (10/page)
          // already bounds the row count, so no internal scroll is needed on top of it.
          height={null}
          scrollX={1200}
          size="small"
          bordered
        />
      </Card>
    );
  };

  const renderLogTimeline = (logs) => {
    if (!logs || logs.length === 0) {
      return <Empty description="No logs available for this batch" className="my-4" />;
    }

    return (
      <Timeline mode="left" className="mt-4">
        {logs.map((log, index) => {
          const statusConfig = getMachineStatusConfig(log.machine_status);

          return (
            <Timeline.Item
              key={log.log_id}
              color={statusConfig.color}
              dot={getLogTypeIcon(log.log_type)}
            >
              <Card
                size="small"
                style={{
                  backgroundColor: statusConfig.bgColor,
                  borderColor: statusConfig.borderColorVal,
                  border: `1px solid ${statusConfig.borderColorVal}`,
                }}
              >
                <div className="flex justify-between items-start mb-2">
                  <Space>
                    <Tag color={statusConfig.color} className="font-semibold">
                      Stage {log.stage_number}
                    </Tag>
                    <Text strong className="text-sm" style={{ color: textColor }}>
                      {log.stage_name}
                    </Text>
                  </Space>
                  <Tag color="blue" className="text-xs">
                    {log.log_type?.replace(/_/g, ' ')}
                  </Tag>
                </div>

                <Descriptions
                  size="small"
                  column={2}
                  className="mb-2 rounded"
                  contentStyle={{ backgroundColor: timelineDescBgColor, color: textColor }}
                  labelStyle={{ backgroundColor: timelineDescLabelBg, color: secondaryTextColor }}
                >
                  <Descriptions.Item label="Action">
                    <span style={{ color: textColor }}>{log.action_type}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={statusConfig.color}>{log?.machine_status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Timestamp">
                    <span style={{ color: textColor }}>
                      {formatDateTime(log.timestamp, 'DD-MMM-YYYY HH:mm:ss')}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Performed By">
                    <Space size={4} style={{ color: textColor }}>
                      <User size={14} />
                      {log.performed_by?.employee_name}
                    </Space>
                  </Descriptions.Item>
                  {log.start_time && (
                    <Descriptions.Item label="Start Time">
                      <span style={{ color: textColor }}>
                        {formatDateTime(log.start_time, 'DD-MMM-YYYY HH:mm')}
                      </span>
                    </Descriptions.Item>
                  )}
                  {log.end_time && (
                    <Descriptions.Item label="End Time">
                      <span style={{ color: textColor }}>
                        {formatDateTime(log.end_time, 'DD-MMM-YYYY HH:mm')}
                      </span>
                    </Descriptions.Item>
                  )}
                  {log.cleaning_type && (
                    <Descriptions.Item label="Cleaning Type" span={2}>
                      <span style={{ color: textColor }}>
                        {log.cleaning_type.name} ({log.cleaning_type.category})
                      </span>
                    </Descriptions.Item>
                  )}
                  {log.verification_method && (
                    <Descriptions.Item label="Verification Method">
                      <span style={{ color: textColor }}>{log.verification_method}</span>
                    </Descriptions.Item>
                  )}
                  {log.verification_results && (
                    <Descriptions.Item label="Verification Results">
                      <span style={{ color: textColor }}>{log.verification_results}</span>
                    </Descriptions.Item>
                  )}
                  {log.cleaning_agents_used && (
                    <Descriptions.Item label="Cleaning Agents">
                      <span style={{ color: textColor }}>{log.cleaning_agents_used}</span>
                    </Descriptions.Item>
                  )}
                  {log.duration_minutes && (
                    <Descriptions.Item label="Duration">
                      <Tag color="blue">{log.duration_minutes} minutes</Tag>
                    </Descriptions.Item>
                  )}
                  {log.approval_comments && (
                    <Descriptions.Item label="Approval Comments" span={2}>
                      <span style={{ color: textColor }}>{log.approval_comments}</span>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                {log.remarks && (
                  <Alert
                    message="Remarks"
                    description={log.remarks}
                    type="info"
                    showIcon
                    className="text-xs"
                    style={{
                      backgroundColor: isDark ? '#1e3a5f' : undefined,
                      borderColor: isDark ? '#1e40af' : undefined,
                    }}
                  />
                )}
              </Card>
            </Timeline.Item>
          );
        })}
      </Timeline>
    );
  };

  const renderBatchDetailsDrawer = () => {
    if (!selectedBatch) return null;

    const statusConfig = getStatusConfig(selectedBatch.batch_status);

    return (
      <Drawer
        title={
          <Space>
            <Package size={20} className="text-blue-600" />
            <span style={{ color: textColor }}>Batch Details: {selectedBatch.batch_number}</span>
          </Space>
        }
        placement="right"
        width={720}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedBatch(null);
        }}
        open={drawerVisible}
        styles={{
          header: { backgroundColor: drawerBgColor, borderBottom: `1px solid ${borderColor}` },
          body: { backgroundColor: drawerBgColor },
        }}
      >
        <Card
          className="mb-4 shadow-sm"
          style={{ backgroundColor: cardBgColor, borderColor: borderColor }}
        >
          <Descriptions
            bordered
            size="small"
            column={2}
            contentStyle={{ backgroundColor: descriptionsBgColor, color: textColor }}
            labelStyle={{ backgroundColor: descriptionsLabelBg, color: secondaryTextColor }}
          >
            <Descriptions.Item label="Batch Number" span={2}>
              <Text strong className="text-blue-600">
                {selectedBatch.batch_number}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="MBMR Name">
              <span style={{ color: textColor }}>{selectedBatch.recipe_name}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag
                className="inline-flex items-center gap-1 text-xs px-4 py-1  bg-orange-100 text-orange-700 border border-orange-300"
                color={statusConfig.color}
                icon={statusConfig.icon}
              >
                {statusConfig.text}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Product Name">
              <span style={{ color: textColor }}>{selectedBatch.product_name}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Product Code">
              <span style={{ color: textColor }}>{selectedBatch.product_code}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Start Time" span={2}>
              <span style={{ color: textColor }}>
                {formatDateTime(selectedBatch.batch_start_time, 'DD-MMM-YYYY HH:mm:ss')}
              </span>
            </Descriptions.Item>
            {selectedBatch.batch_end_time && (
              <Descriptions.Item label="End Time" span={2}>
                <span style={{ color: textColor }}>
                  {formatDateTime(selectedBatch.batch_end_time, 'DD-MMM-YYYY HH:mm:ss')}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Duration" span={2}>
              <Tag color="blue" className="text-sm">
                {formatDuration(selectedBatch.batch_start_time, selectedBatch.batch_end_time)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Total Logs" span={2}>
              <Badge count={selectedBatch.logs?.length || 0} showZero color="blue" />
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Divider orientation="left" style={{ borderColor: borderColor }}>
          <Space style={{ color: textColor }}>
            <Activity size={18} />
            Activity Timeline
          </Space>
        </Divider>

        <div
          className="overflow-y-auto pr-2"
          style={{
            maxHeight: 'calc(100vh - 500px)',
          }}
        >
          {renderLogTimeline(selectedBatch.logs)}
        </div>
      </Drawer>
    );
  };

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Spin spinning={loading} tip="Loading equipment logs...">
        <Card
          className="border border-gray-300 shadow-md"
          bordered={false}
          style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff', minHeight: 'calc(100vh - 120px)' }}
          title={
            <div className="flex items-center gap-2">
              <Activity className="text-blue-600" size={24} />
              <Title
                level={4}
                style={{
                  color: textColor,
                  margin: 0,
                  padding: 0,
                }}
              >
                {t('Equipment Logs')}
              </Title>
            </div>
          }
          extra={
            <div className="flex items-center gap-2 flex-wrap">
              <Select
                showSearch
                placeholder="Select equipment"
                value={selectedEquipment}
                onChange={handleEquipmentChange}
                style={{ width: 260 }}
                size="middle"
                allowClear
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {equipmentList?.map((equipment) => (
                  <Option key={equipment.equipment_id} value={equipment.equipment_id}>
                    {equipment.equipment_name}
                    {equipment.equipment_code && ` (${equipment.equipment_code})`}
                  </Option>
                ))}
              </Select>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                style={{ width: 260 }}
                size="middle"
                format="DD-MMM-YYYY"
                placeholder={['Start Date', 'End Date']}
                disabled={!selectedEquipment}
                allowClear={false}
              />
              <Button
                type="primary"
                icon={<RefreshCw size={14} />}
                onClick={handleRefresh}
                disabled={!selectedEquipment}
                size="middle"
              >
                Load Logs
              </Button>
              {/* Explicit height matches Ant's "middle" control height (32px) so this
                  tag reads as the same size as the Select/DatePicker/Button beside it —
                  a plain Tag is naturally shorter than a form control by default. */}
              <Tag
                color="blue"
                className="inline-flex items-center gap-1"
                style={{ height: 32, padding: '0 12px', fontSize: 13, margin: 0 }}
              >
                <Activity size={14} />
                <span>Equipment Tracking</span>
              </Tag>
            </div>
          }
        >
          {!selectedEquipment && !logData && (
            <Card className="mt-6">
              <Empty description="Please select an equipment to view logs" />
            </Card>
          )}

          {logData && (
            <>
              {renderSummary()}
              {renderEquipmentDetails()}
              {renderBatchLogs()}
            </>
          )}
        </Card>

        {renderBatchDetailsDrawer()}
      </Spin>
    </div>
  );
};

export default EquipmentLogs;
